<script setup lang="ts">
// Full-bleed photographic banner band — the high-visual-reward moment
// between content sections. A brand-tinted overlay keeps text readable;
// desktop gets a fixed-attachment parallax feel (mobile browsers handle
// fixed backgrounds badly, so they scroll normally).
import { computed } from 'vue'

const props = withDefaults(
    defineProps<{
        /** Bundled image URL (import it and pass the result). */
        image: string
        /** CSS background for the tint layer, e.g. a brand gradient. */
        overlay: string
        /** Text alignment of the content block. */
        align?: 'left' | 'center'
        /** Disable the desktop parallax (fixed attachment). */
        noParallax?: boolean
    }>(),
    { align: 'center', noParallax: false },
)

const bgStyle = computed(() => ({ backgroundImage: `url(${props.image})` }))
const overlayStyle = computed(() => ({ background: props.overlay }))
</script>

<template>
    <section class="photo-band" :class="{ parallax: !noParallax, left: align === 'left' }" :style="bgStyle">
        <div class="tint" :style="overlayStyle"></div>
        <div class="site-content inner">
            <slot />
        </div>
    </section>
</template>

<style scoped lang="scss">
.photo-band {
    position: relative;
    background-size: cover;
    background-position: center;
    padding: clamp(5rem, 14vw, 9rem) var(--site-space-lg);
    overflow: hidden;

    &.parallax {
        @media (min-width: 900px) and (prefers-reduced-motion: no-preference) {
            background-attachment: fixed;
        }
    }

    .tint {
        position: absolute;
        inset: 0;
    }

    .inner {
        position: relative;
        z-index: 1;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--site-space-md);
    }

    &.left .inner {
        text-align: left;
        align-items: flex-start;
    }
}
</style>
